import { clsx } from "clsx";
import { FC, MouseEvent, MouseEventHandler, useState } from "react";
import { formatRelativeTime } from "../../utils/dateFormat";
import { Button } from "../Button/Button";
import {
  Icon,
  type IconName,
  iconPaintProps,
  type MonoIconName,
} from "../Icons";
import { Tooltip } from "../Tooltip/Tooltip";
import { Body } from "../Typography/Body";
import { Heading } from "../Typography/Heading";
import "./NotificationBar.css";

export type NotificationStatus = "UNREAD" | "READ" | "SENT" | "PENDING";

/** Icon config for a notification type — same shape as TimelineItemTypeConfig */
export type NotificationTypeConfig = {
  bgColor: string;
  label?: string;
} & (
  | { icon: IconName; iconColor?: never }
  // Tinting demands a mono glyph: a colour glyph paints from its own palette
  // and would ignore `iconColor`.
  | { icon: MonoIconName; iconColor?: string }
);

export interface NotificationCardData {
  id: string;
  appointmentRecordID: string;
  notificationType: string;
  title: string;
  body: string;
  status: NotificationStatus;
  createdAt: string;
}

export interface NotificationCardProps {
  notification: NotificationCardData;
  /** Icon config for this notification's type */
  typeConfig?: NotificationTypeConfig;
  onClick?: MouseEventHandler<HTMLDivElement>;
  /** Fired when the user clicks the mark-as-read button (checkmark on the unread dot) */
  onMarkRead?: (notification: NotificationCardData) => Promise<void> | void;
  /**
   * Fired when the user clicks the mark-as-unread button on a read item
   * (bell icon, revealed on hover).
   */
  onMarkUnread?: (notification: NotificationCardData) => Promise<void> | void;
  className?: string;
}

/**
 * NotificationCard — Renders a single notification item inside the NotificationBar.
 */
export const NotificationCard: FC<NotificationCardProps> = ({
  notification,
  typeConfig,
  onClick,
  onMarkRead,
  onMarkUnread,
  className,
}) => {
  const isUnread = notification.status === "UNREAD";
  const [isMarkingRead, setIsMarkingRead] = useState(false);
  const [isMarkingUnread, setIsMarkingUnread] = useState(false);

  const handleMarkRead = async (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setIsMarkingRead(true);
    try {
      await onMarkRead?.(notification);
    } finally {
      setIsMarkingRead(false);
    }
  };

  const handleMarkUnread = async (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setIsMarkingUnread(true);
    try {
      await onMarkUnread?.(notification);
    } finally {
      setIsMarkingUnread(false);
    }
  };

  const renderStatusAction = () => {
    if (isUnread && onMarkRead) {
      return (
        <div className="notification-card__mark-read-wrapper">
          <span className="notification-card__dot" />
          <Tooltip content="Mark as read" placement="bottom-end">
            <Button
              icon={
                <Icon name="checkmark" fill="var(--indigo-icon-tertiary)" />
              }
              size="sm"
              variant="secondary"
              loading={isMarkingRead}
              onClick={handleMarkRead}
              className="notification-card__mark-read-btn"
              aria-label="Mark as read"
            />
          </Tooltip>
        </div>
      );
    }

    if (isUnread) {
      return <span className="notification-card__dot" />;
    }

    if (onMarkUnread) {
      return (
        <div className="notification-card__mark-unread-wrapper">
          <Tooltip content="Mark as unread" placement="bottom-end">
            <Button
              icon={
                <Icon name="notification" fill="var(--indigo-icon-tertiary)" />
              }
              size="sm"
              variant="secondary"
              loading={isMarkingUnread}
              onClick={handleMarkUnread}
              className="notification-card__mark-unread-btn"
              aria-label="Mark as unread"
            />
          </Tooltip>
        </div>
      );
    }

    return null;
  };

  return (
    <div
      className={clsx("notification-card", className)}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          onClick?.(e as unknown as React.MouseEvent<HTMLDivElement>);
        }
      }}
    >
      {/* Top row: icon + title + unread dot / mark-read button */}
      <div className="notification-card__header">
        {typeConfig && (
          <span
            className="notification-card__icon"
            style={{ backgroundColor: typeConfig.bgColor }}
          >
            <Icon
              {...iconPaintProps(typeConfig.icon, typeConfig.iconColor)}
              size="xs"
            />
          </span>
        )}
        <Heading
          level={3}
          className={clsx(
            "notification-card__title",
            !isUnread && "notification-card__title--read",
          )}
        >
          {notification.title}
        </Heading>
        {renderStatusAction()}
      </div>

      {/* Body text + timestamp on same row */}
      <div className="notification-card__body-row">
        <Body
          as="span"
          size="sm"
          className={clsx(
            "notification-card__body",
            !isUnread && "notification-card__body--read",
          )}
        >
          {notification.body}
        </Body>
        <Body
          as="span"
          size="sm"
          className={clsx(
            "notification-card__time",
            !isUnread && "notification-card__time--read",
          )}
        >
          {formatRelativeTime(notification.createdAt)}
        </Body>
      </div>
    </div>
  );
};
